import { MedicalSpecialistsRecommendation } from './medicalSpecialist';
import { Search } from './search';
import { parse } from 'date-fns';
import {
  DayOfWeek,
  MedicalInstitution,
  MedicalInstitutionOpeningHour,
  MedicoHospital,
  MedicoHospitalVisitingGuideFirstStep,
} from '../../gen/medinavi-backend-consumer-graphql/graphql';

/**
 * medicalInstitutionの診療開始時間を返す
 *
 * 1. medicoHospital
 * 2. eparkFacility
 * 3. medicalInstitution
 *
 * の順で診療開始時間を探して見つかったらそれを返す。全て見つからなかったらnullを返す。
 */
export function getOpenHoursText(
  medicalInstitution: Pick<MedicalInstitution, 'openingHourTextAm' | 'openingHourTextPm' | 'openingHourTextOthers'> & {
    medicoHospital: Pick<MedicoHospital, 'amOpenTime' | 'pmOpenTime' | 'amCloseTime' | 'pmCloseTime'> | null;
  },
): string | null {
  const medicoOpeningHourText = ((): string | null => {
    const mh = medicalInstitution.medicoHospital;
    if (mh === null) return null;
    const isHoursNull = !mh.amOpenTime || !mh.amCloseTime || !mh.pmCloseTime || !mh.pmOpenTime;
    return isHoursNull ? null : `${mh.amOpenTime}〜${mh.amCloseTime}\n${mh.pmOpenTime}〜${mh.pmCloseTime}`;
  })();
  if (medicoOpeningHourText) {
    return medicoOpeningHourText;
  }

  if (
    medicalInstitution.openingHourTextAm ||
    medicalInstitution.openingHourTextPm ||
    medicalInstitution.openingHourTextOthers
  ) {
    return [
      medicalInstitution.openingHourTextAm,
      medicalInstitution.openingHourTextPm,
      medicalInstitution.openingHourTextOthers,
    ]
      .filter(Boolean)
      .join('\n');
  }

  return null;
}

export function getAccessText(
  medicalInstitution: Pick<MedicalInstitution, 'transportation' | 'nearestStation' | 'timeFromStation'>,
): string | null {
  if (medicalInstitution.transportation) {
    return [medicalInstitution.transportation, medicalInstitution.nearestStation, medicalInstitution.timeFromStation]
      .filter(Boolean)
      .join(' ');
  }

  return null;
}

export function hasMatchedSpecialist(
  medicalInstitution: {
    medicalSpecialists: readonly { medicalSpecialist: { id: number } }[];
  },
  medicalSpecialistsRecommendation: MedicalSpecialistsRecommendation,
): boolean {
  const searcher = new Search(medicalInstitution.medicalSpecialists.map((sp) => sp.medicalSpecialist.id));
  return searcher.searchBy(medicalSpecialistsRecommendation.medicalSpecialists.map((sp) => sp.id));
}

export function getDisplayAddress(
  medicalInstitution: Pick<MedicalInstitution, 'address'> & {
    medicoHospital: Pick<MedicoHospital, 'address'> | null;
  },
): string {
  return medicalInstitution.medicoHospital?.address || medicalInstitution.address;
}

/**
 * 診療可能な曜日をSetで返す
 */
export function getOpeningDays(medicalInstitution: {
  openingHours: readonly Pick<MedicalInstitutionOpeningHour, 'dayOfWeek'>[];
}): Set<DayOfWeek> {
  const set = new Set<DayOfWeek>();
  medicalInstitution.openingHours.forEach((hours) => {
    set.add(hours.dayOfWeek);
  });
  return set;
}

/**
 * 17時より遅い時間に診療可能な日がある場合に一番遅い時間を返す。すべての診療日の診療時間が17時以前の場合はnullを返す
 * ユビーリンクに午後診療終了時間がある場合はそちらを優先する
 *
 * @example
 * institution.openingHours // => [{ closeTime: "17:00" }, { closeTime: "19:00" }, { closeTime: "18:00" }]
 * institution.getLateCloseTime() // => "19:00"
 *
 * // ユビーリンクに午後診療終了時間がある場合はそちらを優先する
 * institution.openingHours // => [{ closeTime: "17:00" }, { closeTime: "19:00" }, { closeTime: "18:00" }]
 * institution.medicoHospital.pmCloseTime // => "18:00"
 * institution.getLateCloseTime() // => "18:00"
 */
export function getLateCloseTime(medicalInstitution: {
  medicoHospital: Pick<MedicoHospital, 'pmCloseTime'> | null;
  openingHours: readonly Pick<MedicalInstitutionOpeningHour, 'closeTime'>[];
}): string | null {
  const thresholdHour = 17;
  const isLateTime = (date: Date): boolean => {
    const hour = date.getHours();
    const minute = date.getMinutes();
    return hour > thresholdHour || (hour === thresholdHour && minute > 0);
  };

  // ユビーリンクの午後診療終了時間にパース可能な時間のテキストがあればそちらを優先する
  const pmCloseTime = medicalInstitution.medicoHospital?.pmCloseTime;
  const pmCloseDate = parse(pmCloseTime ?? '', 'HH:mm', new Date());
  if (pmCloseTime && !Number.isNaN(pmCloseDate.valueOf())) {
    if (isLateTime(pmCloseDate)) {
      return pmCloseTime;
    } else {
      // ユビーリンクの午後診療終了時間に17時以前のデータ入っている場合はそちらのデータを優先するためopeningHoursは見ないでnullを返す
      return null;
    }
  }

  // ユビーリンクにデータなければウェルネスのデータを使う
  let current: { date: Date | null; closeTime: string | null } = {
    date: null,
    closeTime: null,
  };
  medicalInstitution.openingHours.forEach(({ closeTime }) => {
    const date = parse(closeTime, 'HH:mm', new Date());
    if (Number.isNaN(date.valueOf())) return;

    if (isLateTime(date) && (current.date === null || date > current.date)) {
      current = { date, closeTime };
    }
  });
  return current.closeTime;
}

/**
 * Web予約が可能である場合にtrueを、そうでない場合にfalseを返します。
 * Web予約が可能であるとは、来院案内1stステップでWeb予約を案内しているということであり、それ以外の意味はありません。
 */
export function isWebReservationAvailable(medicalInstitution: {
  medicoHospital: {
    visitingGuide: {
      firstStep: Pick<MedicoHospitalVisitingGuideFirstStep, 'type' | 'reservationUrl'>;
    };
  } | null;
}): boolean {
  const firstStep = medicalInstitution.medicoHospital?.visitingGuide?.firstStep;
  return (
    firstStep != null &&
    firstStep.type === 'WEB_RESERVATION' &&
    firstStep.reservationUrl != null &&
    firstStep.reservationUrl.length > 0
  );
}
